import {
    Box,
    SelectChangeEvent,
    Typography,
} from "@mui/material";
import { LoginLayout } from "../commonComponents";
import {
    LoginWithOtpResponseBody, Roles, VerifyLoginEmailResponseBody
} from "../../redux-store/types/api-types";
import { useHistory } from "react-router";
import {
    Form,
    Formik,
    FormikProps
} from "../../lib/formik/Formik";
import MFSelectField from "../../lib/formik/SelectField";
import * as yup from 'yup';
import { useDispatch } from "react-redux";
import {
    verifyLoginEmail
} from "../../redux-store/actions/auth";
import { useEffect, useMemo, useRef, useState } from "react";
import { MFSubmitButton, MFTextField } from "../../lib/formik";

interface Values {
    role: Partial<Roles> | null;
    email: string;
}

const validationSchema = yup.object().shape({
    role: yup
        .object()
        .nullable()
        .test(
            "role-required",
            "Login Type is required",
            function (value) {
                return !!value
            }
        ),
    email: yup
        .string()
        .required("Email is required")
})

export default function SelectRole(): JSX.Element {
    const history = useHistory()
    const dispatch = useDispatch()

    const routerState = history.location.state as LoginWithOtpResponseBody

    const [isFormSubmitting, setFormSubmitting] = useState<boolean>(false)

    const formikRef = useRef<FormikProps<Values>>(null);

    const initialValues = useMemo<Values>(() => ({
        role:
            (routerState?.roles ?? []).length === 1
                ? routerState.roles[0]
                : null,
        email: "",
    }), [routerState]);

    useEffect(() => {
        const emailElement = document.getElementsByName('email');

        if (emailElement?.[0]) {
            emailElement[0]?.focus();
        }
    }, []);

    const handleSubmit = async (values: Values) => {
        try {
            setFormSubmitting(true)

            const response = await dispatch(verifyLoginEmail({
                phone: routerState?.unMaskedPhone ?? "",
                countryCode: routerState?.countryCode ?? "",
                email: values?.email ?? "",
                userId: Number(routerState?.userId ?? ""),
                userRole: values?.role?.roleId ?? ""
            }) as unknown as Promise<VerifyLoginEmailResponseBody>)


            if (!response) {
                return
            }

            history.push('verify-otp', {
                ...(response || {}),
            });
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setFormSubmitting(false)
        }
    }

    const handleRoleChange = ({ target: { value: selectedRoleId } }: SelectChangeEvent<unknown>) => {
        if (!formikRef?.current) {
            return
        }

        const { setFieldValue } = formikRef?.current ?? {}

        const selectedRoleObj = (routerState?.roles ?? []).find(obj => obj.roleId === selectedRoleId)

        setFieldValue("role", selectedRoleObj)
    }

    const isMultipleRoles = (routerState?.roles ?? []).length > 1

    return (
        <LoginLayout LoginText="">
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                enableReinitialize
            >
                {({ values }) => (
                    <Form>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                rowGap: "10px"
                            }}
                        >
                            <Typography
                                variant="h1"
                                sx={{
                                    color: 'primary.main',
                                    textAlign: 'center',
                                }}
                            >
                                Email and Login Type
                            </Typography>
                            {isMultipleRoles && (
                                <Typography
                                    component="span"
                                    variant="subtitle_500"
                                    sx={{ textAlign: "justify" }}
                                >
                                    <Typography
                                        component="span"
                                        variant="subtitle_500"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Note:{" "}
                                    </Typography>
                                    Multiple login types are associated with this mobile number. Please enter your email and select the appropriate login type.
                                </Typography>
                            )}
                        </Box>
                        <Box
                            sx={{
                                marginTop: isMultipleRoles ? "25px" : "50px",
                                display: "flex",
                                flexDirection: "column",
                                rowGap: "30px"
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <MFTextField
                                    name="email"
                                    label="Enter Email *"
                                    placeholder="Enter email"
                                    value={values.email ?? ""}
                                    autoComplete="on"
                                />
                                <MFSelectField
                                    name="role"
                                    label="Select Login Type *"
                                    value={values?.role?.roleId ?? ""}
                                    items={(routerState?.roles ?? []).map((obj) => ({
                                        ...obj,
                                        key: obj?.roleName ? obj.roleName : (obj?.roleId ?? ""),
                                        value: obj?.roleId ?? ""
                                    }))}
                                    onChange={handleRoleChange}
                                />
                            </Box>
                            <MFSubmitButton
                                label="Submit"
                                loadingEnable={isFormSubmitting}
                            />
                        </Box>
                    </Form>
                )}
            </Formik>
        </LoginLayout >
    )
}